import { addDays, format } from 'date-fns';
import { Image } from 'expo-image';
import { t } from 'i18next';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type {
  Address,
  SubscriptionCar,
  SubscriptionDuration,
} from '@/api/subscriptions/types';
import {
  getCheapestModelKey,
  useSubscriptionOptions,
} from '@/api/subscriptions/use-subscription-options';
import { formatNumber, useCurrentOrganization } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import useSubscriptionStore from '@/stores/use-subscription-store';
import { Text } from '@/ui';
import Alert, { AlertType } from '@/ui/alert';
import { Calendar } from '@/ui/calendar';
import { SubscriptionPeriodItem } from '@/ui/card-item/subscription-period-item';
import GoogleAutocomplete from '@/ui/google-autocomplete';
import { Icon } from '@/ui/icon';
import { Car, Search } from '@/ui/icons';
import { Calendar as CalendarIcon } from '@/ui/icons/calendar';
import { Repeat } from '@/ui/icons/repeat';
import { Loader } from '@/ui/loader';

export default function ChooseLocationTime() {
  const { styles, theme } = useStyles(stylesheet);

  const address = useSubscriptionStore((state) => state.address);
  const [placeDetails, setPlaceDetails] = useState<any | null>(null);
  const startDatetime = useSubscriptionStore((state) => state.startDatetime);
  const period = useSubscriptionStore((state) => state.period);
  const setAddress = useSubscriptionStore((state) => state.setAddress);
  const setStartDatetime = useSubscriptionStore(
    (state) => state.setStartDatetime
  );
  const setPeriod = useSubscriptionStore((state) => state.setPeriod);
  const setCar = useSubscriptionStore((state) => state.setCar);
  const setModelName = useSubscriptionStore((state) => state.setModelName);
  const setTrimName = useSubscriptionStore((state) => state.setTrimName);
  const { isEVFMC } = useCurrentOrganization();
  const [showCalendar, setShowCalendar] = useState(false);

  const {
    data: subscriptionOptions,
    isLoading,
    isFetched,
  } = useSubscriptionOptions({
    variables: {
      address: address?.description,
      startDate: startDatetime!,
    },
    enabled: !!address && !!startDatetime,
  });

  useEffect(() => {
    if (isEVFMC && address?.description && startDatetime) {
      setPeriod('weekly');
      setCar(null);
      setModelName('');
      setTrimName('');
    }
  }, [isEVFMC, address?.description, startDatetime]);

  useEffect(() => {
    if (!subscriptionOptions || !period || subscriptionOptions[period]) {
      return;
    }

    setPeriod('');
    setCar(null);
    setModelName('');
    setTrimName('');
  }, [subscriptionOptions, period]);

  const minDate = useMemo(() => {
    return addDays(new Date(), 1);
  }, []);

  const formattedAddress = useMemo((): any => {
    if (!placeDetails) return;

    let street = '';
    let city = '';
    let state = '';
    let zipcode = '';
    let country = '';

    placeDetails.addressComponents.map((component: any) => {
      if (component.types.includes('street_number')) {
        street = component.longText || component.long_name;
      }

      if (component.types.includes('route')) {
        street += component.longText
          ? ` ${component.longText}`
          : ` ${component.long_name}`;
      }

      if (component.types.includes('locality')) {
        city = component.longText || component.long_name;
      }

      if (component.types.includes('administrative_area_level_1')) {
        state = component.shortText || component.short_name;
      }

      if (component.types.includes('postal_code')) {
        zipcode = component.longText || component.long_name;
      }

      if (component.types.includes('country')) {
        country = component.longText || component.long_name;
      }
    });

    return {
      street,
      city,
      state,
      zipcode,
      country,
    };
  }, [placeDetails]);

  const showSubscriptionPeriod = useMemo(() => {
    if (isEVFMC) {
      return false;
    }

    return subscriptionOptions && address && startDatetime;
  }, [isEVFMC, subscriptionOptions, address, startDatetime]);

  const handlePlaceSelected = async (place: Address) => {
    try {
      setPlaceDetails(place);
    } finally {
      setAddress({
        ...place,
      });
      setCar(null);
    }
  };

  useEffect(() => {
    if (!formattedAddress) return;

    const { street, city, state, zipcode, country } = formattedAddress;

    mixpanelService.track('Subscription Location Selected', {
      'Street Name': street,
      City: city,
      State: state,
      Zipcode: zipcode,
      Country: country,
    });
  }, [formattedAddress]);

  useEffect(() => {
    if (!formattedAddress) return;

    const { street, city, state, zipcode, country } = formattedAddress;

    if (!subscriptionOptions && isFetched) {
      mixpanelService.track('Subscription No Cars Available', {
        'Street Name': street,
        City: city,
        State: state,
        Zipcode: zipcode,
        Country: country,
        Date: startDatetime,
      });
    }
  }, [subscriptionOptions, isFetched]);

  return (
    <View style={{ flex: 1 }}>
      <View
        style={{
          flexGrow: 1,
          width: '100%',
          gap: 24,
        }}
      >
        <View style={{ gap: 16, zIndex: 2 }}>
          <Text tx="subscriptions.locationAndStartDate" type="bodyRegular" />

          <View style={styles.locationAndDateContainer}>
            <View
              style={{
                flex: 1,
                zIndex: 2,
                backgroundColor: theme.colors.background_2,
                borderRadius: 8,
                gap: 16,
              }}
            >
              <GoogleAutocomplete
                address={address}
                onPress={handlePlaceSelected}
                placeholderTextColor={theme.colors.text.secondary}
                placeholder="Search for an address"
                leftIcon={
                  <Icon
                    type="naked"
                    icon={<Search />}
                    fillColor={theme.colors.icon.color}
                    width={22}
                    height={22}
                  />
                }
              />
            </View>

            <Pressable
              onPress={() => {
                setShowCalendar(true);
              }}
              style={styles.calendarButton}
            >
              <Icon
                type="naked"
                icon={<CalendarIcon />}
                fillColor={theme.colors.icon.color}
                width={22}
                height={22}
              />

              {startDatetime ? (
                <View>
                  <Text type="bodyRegular" style={{ fontSize: 14 }}>
                    {format(startDatetime, 'MMM dd yyyy')}
                  </Text>
                </View>
              ) : (
                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                  tx="calendar.selectStartDate"
                />
              )}
            </Pressable>
          </View>
        </View>

        {!isFetched && !isLoading ? (
          <View
            style={{
              minHeight: 250,
              justifyContent: 'center',
            }}
          >
            <View
              style={{
                gap: 16,
                alignItems: 'center',
              }}
            >
              <Icon
                type="dressed"
                icon={<Car />}
                fillColor={theme.colors.icon.color}
                width={24}
                height={24}
              />

              <View style={{ alignItems: 'center', gap: 8 }}>
                <Text type="bodyBold">Where and When?</Text>

                <Text
                  type="bodyRegular"
                  style={{
                    color: theme.colors.text.secondary,
                    textAlign: 'center',
                  }}
                >
                  Select your pickup location and start date to see available
                  cars and pricing.
                </Text>
              </View>
            </View>
          </View>
        ) : null}

        {isLoading && (
          <View
            style={{
              width: '100%',
              alignItems: 'center',
              backgroundColor: theme.colors.background_1,
            }}
          >
            <Loader style={{ width: 150, height: 150 }} />
          </View>
        )}

        {showSubscriptionPeriod ? (
          <View style={{ gap: 16 }}>
            <View style={{ gap: 8 }}>
              <Text tx="subscriptions.subscriptionPeriod" type="bodyBold" />

              <Text
                tx="subscriptions.commitmentBody"
                type="caption1"
                style={{ color: theme.colors.text.secondary }}
              />
            </View>

            <Alert
              type={AlertType.BRAND}
              icon={<Repeat />}
              iconWidth={22}
              iconHeight={16}
              iconContainerStyle={{ marginTop: 4 }}
              title={t('subscriptions.autoRenewTitle')}
              body={t('subscriptions.autoRenewBody')}
            />

            <View style={styles.subscriptionPeriodsContainer}>
              {Object.keys(subscriptionOptions).map(
                (key: SubscriptionDuration) => {
                  const value = subscriptionOptions[key as any];
                  const cheapestModelKey = getCheapestModelKey(value);

                  if (typeof value !== 'object' || !cheapestModelKey) {
                    return null;
                  }

                  const cheapestOption = value[cheapestModelKey]
                    .cheapestOption as SubscriptionCar;

                  return (
                    <SubscriptionPeriodItem
                      key={key}
                      title={value.title}
                      subtitle={value.caption}
                      mostPopular={key === 'monthly'}
                      isFreeDelivery={key !== 'weekly'}
                      cost={formatNumber(
                        key === '6Months'
                          ? cheapestOption.cost / 6
                          : cheapestOption.cost
                      )}
                      unit={value.unit}
                      active={period === key}
                      onPress={() => {
                        setPeriod(key);
                        setCar(null);
                        setModelName('');
                        setTrimName('');
                      }}
                      style={styles.subscriptionPeriodsItem}
                    />
                  );
                }
              )}
            </View>
          </View>
        ) : null}

        {!subscriptionOptions && isFetched ? (
          <View style={{ alignItems: 'center', gap: 24 }}>
            <Image
              style={{ width: 250, height: 200 }}
              source={
                UnistylesRuntime.themeName === 'dark'
                  ? require('../../../../public/images/empty-road-dark.png')
                  : require('../../../../public/images/empty-road-light.png')
              }
              contentFit="cover"
            />

            <View style={{ alignItems: 'center', gap: 8 }}>
              <Text tx="subscriptions.noCarsAvailable" type="title1" />
              <Text
                tx="subscriptions.noCarsAvailableBody"
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              />
            </View>
          </View>
        ) : null}
      </View>

      <Calendar
        isVisible={showCalendar}
        minDate={minDate}
        onHide={() => {
          setShowCalendar(false);
        }}
        onDateSelected={(startDt) => {}}
        onDatesSelected={(startDt, _endDt) => {
          // Hard-set the subscription start time to 8:00 AM Eastern on the
          // selected day. Using the IANA zone keeps this DST-correct (EDT/EST).
          const startAtEastern = DateTime.fromJSDate(startDt)
            .setZone('America/New_York')
            .set({ hour: 8, minute: 0, second: 0, millisecond: 0 })
            .toJSDate();

          mixpanelService.track('Subscription Date Selected', {
            Date: startAtEastern.toString(),
          });

          setStartDatetime(startAtEastern);
          setShowCalendar(false);
          setCar(null);
        }}
        showTimePicker={false}
        range={false}
        mode="date"
        id="subscription-start-calendar"
        containerStyle={{
          height: '90%',
        }}
        showDesktopConfirm
        headerTitle="Start Date"
      />
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  locationAndDateContainer: {
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    gap: 16,
  },
  calendarButton: {
    flex: {
      md: 1,
    },
    height: 56,
    borderColor: theme.colors.input.default.border,
    borderWidth: 1,
    borderRadius: 8,
    alignItems: 'center',
    paddingLeft: 16,
    flexDirection: 'row',
    gap: 16,
    backgroundColor: theme.colors.background_2,
  },
  subscriptionPeriodsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  subscriptionPeriodsItem: {
    flexBasis: {
      xs: '100%',
      md: '32%',
    },
    maxWidth: {
      xs: '100%',
      md: '32%',
    },
    flexGrow: 1,
  },
}));
